import { useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Brotherhood of Misfits · public apply page
 * A hiring funnel for the vulnerable: no résumé bar, no credit bar,
 * a story and a willingness are enough. The test is the work itself.
 */
export default function BrotherhoodPage() {
  const [manifest, setManifest] = useState(null);
  const [tally, setTally] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", state: "",
    situation: "", story: "", interested_roles: [], availability: "",
  });
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/sage/brotherhood/manifest`).then(r => r.json()).then(setManifest).catch(() => {});
    fetch(`${API}/api/sage/brotherhood/tally`).then(r => r.json()).then(setTally).catch(() => {});
  }, []);

  const toggleRole = (slug) => {
    setForm(f => ({
      ...f,
      interested_roles: f.interested_roles.includes(slug)
        ? f.interested_roles.filter(r => r !== slug)
        : [...f.interested_roles, slug],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.story.trim() || form.interested_roles.length === 0) {
      toast.error("Please share your name, email, a short story, and at least one role you're interested in.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/sage/brotherhood/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
      setSubmitted(data);
      toast.success("You're on the Brotherhood's list.");
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#fbfaf6] text-[#1e1e1e] flex items-center justify-center px-6" data-testid="brotherhood-thanks">
        <div className="max-w-2xl">
          <h1 className="text-4xl font-serif text-[#2c3e2a] border-b-2 border-[#b58c2a] pb-4">
            You're on the Brotherhood's list.
          </h1>
          <p className="mt-6 text-lg font-serif text-[#4a5a48]">
            {submitted.message || "Someone will reach out when a role opens in your area."}
          </p>
          {submitted.application_id && (
            <p className="mt-3 text-sm font-serif italic text-[#7c8a75]" data-testid="application-id">
              Your reference: {submitted.application_id}
            </p>
          )}
          <p className="mt-6 text-base font-serif text-[#2c3e2a]">
            The test is the work itself — not the paperwork.
          </p>
          <a href="/" className="inline-block mt-8 border border-[#2c3e2a] py-2 px-5 rounded font-serif hover:bg-[#2c3e2a] hover:text-[#fbfaf6] transition" data-testid="back-home-link">
            Back home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbfaf6] text-[#1e1e1e]" data-testid="brotherhood-page">
      {/* Giant COMING SOON banner — Founder ruling 2026-08-11 */}
      <div className="bg-[#2c3e2a] text-[#c8b78a] py-6 px-6 text-center" data-testid="brotherhood-status-banner">
        <p className="text-3xl md:text-5xl font-serif font-bold uppercase tracking-wider">
          COMING SOON
        </p>
        <p className="text-sm md:text-base font-serif italic mt-2 text-[#fbfaf6]">
          The Brotherhood is being built. The doors are open for the record. Applications on file.
        </p>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="border-b-2 border-[#b58c2a] pb-4">
          <span className="text-4xl font-serif text-[#2c3e2a]">Brotherhood of Misfits</span>
        </div>
        <p className="mt-6 text-2xl font-serif text-[#2c3e2a] leading-snug max-w-3xl">
          A hiring funnel for the vulnerable. Dignified paid work. No résumé bar.
        </p>
        <p className="mt-3 text-base text-[#4a5a48] font-serif max-w-3xl">
          Every vulnerable person the market throws away is a person the Brotherhood catches and hires.
          The cost we would have spent supporting them from a distance we now spend paying them directly.
          The population that needs our help shrinks the more we hire from it. That is the loop, and it
          is the whole point.
        </p>
      </section>

      {/* Live tally */}
      {tally && (
        <section className="max-w-6xl mx-auto px-6 pb-8" data-testid="brotherhood-tally">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="hires active" value={tally.hires_active || 0} testid="tally-hires" />
            <Tile label="applications pending" value={tally.applications_pending || 0} testid="tally-pending" />
            <Tile label="hours worked" value={Number(tally.hours_worked_total || 0).toFixed(1)} testid="tally-hours" />
            <Tile label="wages paid" value={`$${Number(tally.wages_paid_usd_total || 0).toFixed(2)}`} testid="tally-wages" />
          </div>
        </section>
      )}

      {/* Who qualifies */}
      {manifest && (
        <section className="max-w-6xl mx-auto px-6 pb-8" data-testid="who-qualifies">
          <h2 className="text-2xl font-serif text-[#2c3e2a] border-l-4 border-[#b58c2a] pl-4 mb-4">
            Who qualifies
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-base font-serif text-[#4a5a48]">
            {(manifest.who_qualifies || []).map((s) => (
              <li key={s} className="before:content-['·'] before:text-[#b58c2a] before:mr-2">{s}</li>
            ))}
          </ul>
          <div className="mt-4 text-sm font-serif text-[#4a5a48] bg-[#efeadc] border border-[#d9d3bd] rounded p-4">
            <div className="font-bold text-[#2c3e2a] mb-2">No barriers</div>
            <ul className="space-y-1">
              {(manifest.no_barriers || []).map((b, i) => (
                <li key={i}>· {b}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Roles */}
      {manifest && (
        <section className="max-w-6xl mx-auto px-6 pb-8" data-testid="roles-list">
          <h2 className="text-2xl font-serif text-[#2c3e2a] border-l-4 border-[#b58c2a] pl-4 mb-4">
            Roles open
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(manifest.roles_open || []).map((role) => (
              <div key={role.slug} className="bg-white border border-[#d9d3bd] rounded p-5" data-testid={`role-card-${role.slug}`}>
                <div className="text-lg font-serif font-bold text-[#2c3e2a]">{role.title}</div>
                <div className="text-sm font-serif italic text-[#7c8a75] mt-1">Where: {role.where}</div>
                <p className="mt-3 text-sm font-serif text-[#4a5a48]">{role.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Apply form */}
      <section className="max-w-3xl mx-auto px-6 pb-16" data-testid="apply-form">
        <h2 className="text-2xl font-serif text-[#2c3e2a] border-l-4 border-[#b58c2a] pl-4 mb-6">
          Apply
        </h2>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Your name">
            <input
              required
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
              data-testid="input-name"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email">
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
                data-testid="input-email"
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
                data-testid="input-phone"
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="City">
              <input
                type="text"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
                data-testid="input-city"
              />
            </Field>
            <Field label="State">
              <input
                type="text"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                maxLength={5}
                className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
                data-testid="input-state"
              />
            </Field>
          </div>
          <Field label="Your situation (short — hardship, homeless, disabled, unemployed, etc.)">
            <input
              type="text"
              value={form.situation}
              onChange={(e) => setForm({ ...form, situation: e.target.value })}
              className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
              data-testid="input-situation"
            />
          </Field>
          <Field label="Your story (a short paragraph — anything the person reading this should know)">
            <textarea
              required
              rows={4}
              value={form.story}
              onChange={(e) => setForm({ ...form, story: e.target.value })}
              className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
              data-testid="input-story"
            />
          </Field>
          <Field label="Roles you'd like to be considered for">
            <div className="flex flex-wrap gap-2 mt-1" data-testid="role-picker">
              {(manifest?.roles_open || []).map((role) => (
                <button
                  key={role.slug}
                  type="button"
                  onClick={() => toggleRole(role.slug)}
                  className={
                    form.interested_roles.includes(role.slug)
                      ? "border border-[#2c3e2a] bg-[#2c3e2a] text-[#fbfaf6] rounded px-4 py-2 font-serif"
                      : "border border-[#d9d3bd] bg-white text-[#2c3e2a] rounded px-4 py-2 font-serif hover:border-[#2c3e2a] transition"
                  }
                  data-testid={`role-toggle-${role.slug}`}
                >
                  {role.title}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Days and hours you can work (optional)">
            <input
              type="text"
              value={form.availability}
              onChange={(e) => setForm({ ...form, availability: e.target.value })}
              className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
              placeholder="e.g. weekday mornings, or evenings after 6"
              data-testid="input-availability"
            />
          </Field>
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#2c3e2a] text-[#fbfaf6] py-3 rounded font-serif hover:bg-[#3e4a3c] transition disabled:opacity-60"
            data-testid="submit-application"
          >
            {submitting ? "Sending…" : "Send my application"}
          </button>
        </form>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-8 border-t border-[#d9d3bd]">
        <p className="text-sm font-serif italic text-[#7c8a75]">
          Same fire · different notes. A story and a willingness are enough. The test is the work.
        </p>
      </footer>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-serif text-[#2c3e2a] mb-1">{label}</span>
      {children}
    </label>
  );
}

function Tile({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#d9d3bd] rounded p-4" data-testid={testid}>
      <div className="text-xs font-serif italic text-[#7c8a75] uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-serif mt-1 text-[#2c3e2a]">{value}</div>
    </div>
  );
}
